import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import SkillRequestForm from "./SkillRequestForm";
import SkillRequestCard from "./SkillRequestCard";

const PAGE_SIZE = 10;

const SkillRequestsSection = () => {
    const { t } = useTranslation();
    const [requests, setRequests] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isVoting, setIsVoting] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const { call: fetchRequests, loading: loadingRequests } = useBluelagoon({
        method: "getSkillRequests",
    });
    const { call: submitRequest, loading: submitting } = useBluelagoon({
        method: "createSkillRequest",
    });
    const { call: vote } = useBluelagoon({ method: "voteSkillRequest" });

    const loadRequests = useCallback(async (pageNum = 1, append = false) => {
        const res = await fetchRequests({ limit: PAGE_SIZE, page: pageNum });
        if (res?.data) {
            setRequests((prev) => append ? [...prev, ...res.data] : res.data);
            setTotal(res.total || 0);
            setPage(pageNum);
        }
    }, []);

    useEffect(() => {
        loadRequests();
    }, []);

    const handleLoadMore = () => loadRequests(page + 1, true);

    const handleSubmit = async (data) => {
        const res = await submitRequest(data);
        if (res?.data) {
            setRequests((prev) => [res.data, ...prev]);
            setTotal((prev) => prev + 1);
            toast.success(t("skillsPage.requests.submitted"));
        }
    };

    const handleVote = async (skillRequestId, voteType) => {
        setIsVoting(true);
        const res = await vote({ skillRequestId, voteType });
        if (res?.data) {
            setRequests((prev) =>
                prev.map((r) => {
                    if (r.id !== skillRequestId) return r;
                    const oldVote = r.userVote;
                    const newVote = res.data.voteType;
                    let upvotes = r.upvotes;
                    let downvotes = r.downvotes;
                    if (oldVote === "up") upvotes--;
                    if (oldVote === "down") downvotes--;
                    if (newVote === "up") upvotes++;
                    if (newVote === "down") downvotes++;
                    return { ...r, userVote: newVote, upvotes, downvotes };
                }).sort((a, b) => (b.upvotes - b.downvotes) - (a.upvotes - a.downvotes))
            );
        }
        setIsVoting(false);
    };

    const hasMore = requests.length < total;

    return (
        <div className="bg-gradient-to-r from-indigo-50 via-blue-50 to-purple-50 border border-indigo-200 rounded-xl p-5">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-2xl">💡</span>
                    <div className="text-left">
                        <h2 className="text-lg font-semibold text-gray-900">{t("skillsPage.requests.title")}</h2>
                        <p className="text-sm text-gray-500">{t("skillsPage.requests.subtitle")}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {total > 0 && (
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">{total}</span>
                    )}
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </button>

            {isOpen && (
                <div className="mt-4 space-y-3">
                    <SkillRequestForm onSubmit={handleSubmit} isSubmitting={submitting} />
                    {loadingRequests && requests.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">{t("common.loading")}</p>
                    )}
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {requests.map((request) => (
                            <SkillRequestCard key={request.id} request={request} onVote={handleVote} isVoting={isVoting} />
                        ))}
                    </div>
                    {hasMore && (
                        <button onClick={handleLoadMore} disabled={loadingRequests} className="w-full py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
                            {loadingRequests ? t("common.loading") : t("skillsPage.requests.loadMore")}
                        </button>
                    )}
                    {!loadingRequests && requests.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">{t("skillsPage.requests.empty")}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default SkillRequestsSection;
